import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowClockwise, PencilSimpleLine, Plus, NotePencil, PaperPlaneTilt, ChartLineUp, Envelope, FileText, Key, Receipt, Scroll, Warning, WarningCircle, type Icon } from '@phosphor-icons/react';
import { ImmoButton, LeerZustand, SchalterZeile } from '../../../../components/immobilien/ui';
import { supabase } from '../../../../lib/supabase/client';
import {
  BRIEF_KATEGORIEN, beispielWerte, istSchlichteVorlage, findePlatzhalter, gruppierePlatzhalter, vorschauHtml, type BriefKategorie, type PlatzhalterInfo,
} from '../../../../lib/dokumente/briefVorlagen';
import { BlankoBriefDialog } from './BlankoBriefDialog';
import { BriefVorlageEditor, type VorlageZumBearbeiten } from './BriefVorlageEditor';
import { istAdmin } from '../../../../lib/schreiben/rechte';

interface BriefVorlage {
  id: string; name: string; kategorie: string; beschreibung: string | null; html_template: string;
  platzhalter: PlatzhalterInfo[] | null; ist_standard: boolean;
}

const STIL: Record<BriefKategorie, { icon: Icon; fg: string; bg: string }> = {
  mietvertrag: { icon: FileText, fg: 'var(--info)', bg: 'var(--info-soft)' },
  kuendigung: { icon: Scroll, fg: 'var(--bad)', bg: 'var(--bad-soft)' },
  uebergabeprotokoll: { icon: Key, fg: 'var(--ok)', bg: 'var(--ok-soft)' },
  mieterhoehung: { icon: ChartLineUp, fg: 'var(--acc)', bg: 'var(--acc-soft)' },
  mahnung: { icon: Warning, fg: 'var(--warn)', bg: 'var(--warn-soft)' },
  nebenkostenbrief: { icon: Envelope, fg: '#8a63d2', bg: 'rgba(138,99,210,0.15)' },
  anschreiben: { icon: PaperPlaneTilt, fg: 'var(--info)', bg: 'var(--info-soft)' },
  sonstig: { icon: Receipt, fg: 'var(--ink2)', bg: 'var(--fill2)' },
};

/** Papiervorschau im sandboxed iframe (keine Skripte), Höhe passt sich dem Inhalt an. */
function Papier({ html, titel }: { html: string; titel: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [hoehe, setHoehe] = useState(600);
  const doc = `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;padding:32px 36px;font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.55;color:#1f2937;background:#fff}img{max-width:100%}</style></head><body>${html}</body></html>`;
  const messe = () => { const b = ref.current?.contentDocument?.body; if (b) setHoehe(Math.max(300, b.scrollHeight + 8)); };
  return (
    <iframe ref={ref} title={`Vorschau ${titel}`} srcDoc={doc} sandbox="allow-same-origin" onLoad={messe}
      style={{ width: '100%', maxWidth: 720, height: hoehe, border: 'none', borderRadius: 8, background: '#fff', boxShadow: '0 12px 30px -14px var(--shade)' }} />
  );
}

/** Brief-Vorlagen & Platzhalter (Handoff Dokumente §6.5, Screen 12; Spec 2026-03-17, Syntax {{gruppe.feld}}). */
export function BriefVorlagen({ onMeldung, mobil }: { onMeldung: (text: string, fehler?: boolean) => void; mobil: boolean }) {
  const [vorlagen, setVorlagen] = useState<BriefVorlage[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [runde, setRunde] = useState(0);
  const [kategorie, setKategorie] = useState<BriefKategorie>('mietvertrag');
  const [gewaehltId, setGewaehltId] = useState<string | null>(null);
  const [beispiel, setBeispiel] = useState(true);
  const [blanko, setBlanko] = useState<false | { vorlageId: string | null }>(false);
  const [editor, setEditor] = useState<false | { vorlage: VorlageZumBearbeiten | null }>(false);
  // #586 Etappe 4: anlegen/ändern nur Admins – Mitarbeiter öffnen den Editor nur zum Ansehen
  const [admin, setAdmin] = useState(false);
  useEffect(() => { void istAdmin().then(setAdmin, () => setAdmin(false)); }, []);

  useEffect(() => {
    let aktiv = true;
    supabase.from('dokument_vorlagen').select('id, name, kategorie, beschreibung, html_template, platzhalter, ist_standard').eq('aktiv', true).order('ist_standard', { ascending: false }).order('name')
      .then(({ data, error }) => {
        if (!aktiv) return;
        if (error) { setFehler(error.message); return; }
        const liste = (data ?? []) as BriefVorlage[];
        setVorlagen(liste); setFehler(null);
        const erste = BRIEF_KATEGORIEN.find(([k]) => liste.some(v => v.kategorie === k));
        if (erste) setKategorie(erste[0]);
      });
    return () => { aktiv = false; };
  }, [runde]);

  const inKategorie = useMemo(() => (vorlagen ?? []).filter(v => v.kategorie === kategorie), [vorlagen, kategorie]);
  const vorlage = inKategorie.find(v => v.id === gewaehltId) ?? inKategorie[0] ?? null;
  const werte = useMemo(() => (beispiel ? beispielWerte(new Date()) : null), [beispiel]);
  const gruppen = useMemo(() => (vorlage ? gruppierePlatzhalter(findePlatzhalter(vorlage.html_template), vorlage.platzhalter ?? []) : []), [vorlage]);

  const kopiere = async (key: string) => {
    try { await navigator.clipboard.writeText(`{{${key}}}`); onMeldung(`{{${key}}} kopiert`); }
    catch { onMeldung('Kopieren nicht möglich', true); }
  };

  return (
    <section className="flex flex-col min-w-0" style={{ gap: 12 }} aria-label="Brief-Vorlagen">
      <div className="flex flex-wrap items-end justify-between" style={{ gap: 10 }}>
        <div className="flex flex-col" style={{ gap: 2 }}>
          <h2 className="m-0" style={{ fontSize: 22, fontWeight: 800 }}>Brief-Vorlagen &amp; Platzhalter</h2>
          <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Platzhalter werden beim Erstellen mit Mieter-, Objekt- und Vertragsdaten gefüllt</span>
        </div>
        {/* #539: Blanko-Brief – freier Text auf dem Briefpapier als PDF */}
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          {admin && <ImmoButton icon={Plus} onClick={() => setEditor({ vorlage: null })}>Neue Vorlage</ImmoButton>}
          <ImmoButton art="akzent" icon={PencilSimpleLine} onClick={() => setBlanko({ vorlageId: null })}>Blanko-Brief schreiben</ImmoButton>
        </div>
      </div>
      {editor && (
        <BriefVorlageEditor vorlage={editor.vorlage} startKategorie={kategorie} onSchliessen={() => setEditor(false)}
          onGespeichert={(m) => { setEditor(false); onMeldung(m); setRunde((r) => r + 1); }} />
      )}
      {blanko && <BlankoBriefDialog startVorlageId={blanko.vorlageId} onSchliessen={() => setBlanko(false)} onMeldung={onMeldung} />}
      {fehler ? (
        <div role="alert" className="immo-glas flex items-center flex-wrap" style={{ borderRadius: 22, padding: 16, gap: 12 }}>
          <WarningCircle weight="fill" size={22} color="var(--bad)" /><span className="flex-1" style={{ fontSize: 13 }}>{fehler}</span>
          <ImmoButton icon={ArrowClockwise} onClick={() => setRunde(r => r + 1)}>Erneut versuchen</ImmoButton>
        </div>
      ) : !vorlagen ? (
        <div className="animate-pulse" style={{ height: 240, borderRadius: 26, background: 'var(--fill2)' }} aria-busy="true" />
      ) : (
        <>
          <div role="tablist" aria-label="Brief-Typ" className="flex flex-wrap" style={{ gap: 8 }}>
            {BRIEF_KATEGORIEN.map(([k, label]) => {
              const s = STIL[k];
              const an = k === kategorie;
              const anzahl = vorlagen.filter(v => v.kategorie === k).length;
              return (
                <button key={k} type="button" role="tab" aria-selected={an} onClick={() => { setKategorie(k); setGewaehltId(null); }}
                  className="immo-glas flex items-center cursor-pointer" style={{ height: 44, padding: '0 12px 0 6px', gap: 9, borderRadius: 16, border: 'none', fontSize: 13, fontWeight: 700, color: 'var(--ink)', boxShadow: an ? 'inset 0 0 0 2px var(--info)' : undefined, opacity: anzahl ? 1 : 0.6 }}>
                  <span className="flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: 10, background: s.bg }}><s.icon weight="fill" size={16} color={s.fg} /></span>
                  {label}
                  <span className="immo-zahl" style={{ fontSize: 11, fontWeight: 800, color: 'var(--ink3)' }}>{anzahl}</span>
                </button>
              );
            })}
          </div>

          {!vorlage ? (
            <LeerZustand icon={FileText} titel="Keine Vorlage dieses Typs" text="Für diesen Brief-Typ ist noch keine aktive Vorlage angelegt." />
          ) : (
            <div className="grid items-start" style={{ gridTemplateColumns: mobil ? 'minmax(0,1fr)' : 'minmax(0,1fr) minmax(240px, 300px)', gap: 14 }}>
              <div className="flex flex-col min-w-0" style={{ gap: 10 }}>
                {inKategorie.length > 1 && (
                  <div className="flex flex-wrap" style={{ gap: 6 }}>
                    {inKategorie.map(v => (
                      <button key={v.id} type="button" onClick={() => setGewaehltId(v.id)} aria-pressed={v.id === vorlage.id} className="cursor-pointer"
                        style={{ height: 44, padding: '0 14px', borderRadius: 14, border: 'none', fontSize: 13, fontWeight: 700, background: v.id === vorlage.id ? 'var(--acc-fill)' : 'var(--fill2)', color: v.id === vorlage.id ? 'var(--acc-ink)' : 'var(--ink)' }}>
                        {v.name}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 800 }}>{vorlage.name}</span>
                  {istSchlichteVorlage(vorlage.html_template) && (
                    <ImmoButton icon={NotePencil} onClick={() => setEditor({ vorlage })} style={{ marginLeft: 'auto' }}>{admin ? 'Bearbeiten' : 'Ansehen'}</ImmoButton>
                  )}
                  <ImmoButton icon={PencilSimpleLine} onClick={() => setBlanko({ vorlageId: vorlage.id })} style={istSchlichteVorlage(vorlage.html_template) ? undefined : { marginLeft: 'auto' }}>Als Brief verwenden</ImmoButton>
                  {vorlage.ist_standard && <span style={{ height: 22, padding: '0 8px', borderRadius: 11, display: 'inline-flex', alignItems: 'center', fontSize: 10, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink2)' }}>Standard</span>}
                </div>
                {vorlage.beschreibung && <span style={{ fontSize: 13, color: 'var(--ink2)' }}>{vorlage.beschreibung}</span>}
                <div className="flex justify-center" style={{ borderRadius: 26, padding: mobil ? 10 : 22, background: 'var(--fill2)' }}>
                  <Papier html={vorschauHtml(vorlage.html_template, werte)} titel={vorlage.name} />
                </div>
              </div>
              <aside className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 22, padding: 14, gap: 12, ...(mobil ? {} : { position: 'sticky', top: 12 }) }} aria-label="Platzhalter">
                <SchalterZeile an={beispiel} onChange={setBeispiel} label="Mit Beispieldaten anzeigen" />
                {gruppen.length === 0 && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Diese Vorlage enthält keine Platzhalter.</span>}
                {gruppen.map(g => (
                  <div key={g.gruppe} className="flex flex-col" style={{ gap: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{g.gruppe}</span>
                    {g.eintraege.map(p => (
                      <button key={p.key} type="button" onClick={() => void kopiere(p.key)} title="Platzhalter kopieren" className="immo-hover-flaeche flex flex-col text-left cursor-pointer"
                        style={{ minHeight: 44, padding: '4px 8px', borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--ink)', justifyContent: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 700 }}>{p.label}</span>
                        <code style={{ fontSize: 11, color: 'var(--ok)', fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace" }}>{`{{${p.key}}}`}</code>
                      </button>
                    ))}
                  </div>
                ))}
              </aside>
            </div>
          )}
        </>
      )}
    </section>
  );
}
